// Disable unused var lint rule for now because we will add more fields soon.
import React, { useState, useEffect, useMemo, ChangeEvent } from "react"
import { useTranslation, Trans } from "react-i18next"
import { User } from "@terveystalo/web-auth-onewelcome"

import {
  Button,
  Title,
  Lead,
  Select,
  TextField,
  Icon,
  Modal,
} from "@terveystalo/design-system-react-components"
import { Autocomplete } from "./components/Autocomplete"
import authClient, { authConfig } from "./UserManager"
import { config } from "./config"
import { useFormPersistence } from "./hooks/useFormPersistence"
import {
  CustomerRelationshipType,
  getBackend,
  OrganizationType,
  OrganizationSubjectType,
  OrganizationMenuItemType,
  WishToReceiveResponseValue,
  OrganizationFeedback,
  AnonymousOrganizationFeedback,
  ToimipaikkaData,
} from "./client"

const client = getBackend()

interface FormElement {
  value: string
}

interface OrganizationFormProps {
  user: User | null
  isLoggedIn: boolean
}

interface OrganizationFormState {
  organizationType?: string
  organizationName?: string
  businessId?: string
  customerRelationship?: string
  subject?: string
  invoiceNumber?: string
  menuItem?: string
  dateOfOccurrence?: string
  description?: string
  wishToReceiveResponse?: string
  toimipaikka?: string
  name?: string // For anonymous submissions
  email?: string
}

const organizationFormInitialState: OrganizationFormState = {
  organizationType: "",
  organizationName: "",
  businessId: "",
  customerRelationship: "",
  subject: "",
  invoiceNumber: "",
  menuItem: "",
  dateOfOccurrence: "",
  description: "",
  wishToReceiveResponse: "",
  toimipaikka: "",
  name: "", // For anonymous submissions
  email: "",
}

function OrganizationForm({ user: _user, isLoggedIn }: OrganizationFormProps) {
  const { t, i18n } = useTranslation()
  const { formState, setFormState, isRestoring } = useFormPersistence({
    formKey: "organizationForm",
    initialState: organizationFormInitialState,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")
  const [toimipaikkaList, setToimipaikkaList] = useState<ToimipaikkaData[]>([])
  const [showApiErrorModal, setShowApiErrorModal] = useState(false)
  const [apiErrorDetails, setApiErrorDetails] = useState<string>("")
  const [files, setFiles] = useState<File[]>([])
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Update page title when language changes
  React.useEffect(() => {
    document.title = t("pageTitleOrg")
  }, [i18n.language, t])

  // Fetch toimipaikka list on component mount
  useEffect(() => {
    const fetchToimipaikka = async () => {
      try {
        const data = await client.getToimipaikka()

        if (data.data && data.data.length > 0) {
          console.log(`✓ Loaded ${data.data.length} locations for autocomplete`)
          setToimipaikkaList(data.data)
        } else {
          console.error("✗ Received empty location list from API")
          setToimipaikkaList([])
          setApiErrorDetails("No location data received from server")
          setShowApiErrorModal(true)
        }
      } catch (error) {
        // Build detailed error message
        let errorDetails = "Unknown error"
        if (error && typeof error === "object") {
          if ("code" in error) {
            errorDetails = (error as { code?: string }).code || errorDetails
          }
          if ("message" in error) {
            const message = (error as { message?: string }).message
            if (message) errorDetails = message
          }
          if ("response" in error) {
            const response = (error as { response?: { status?: number } }).response
            if (response?.status) {
              errorDetails = `HTTP ${response.status}: ${errorDetails}`
            }
          }
        }

        console.error(`✗ Failed to fetch locations: ${errorDetails}`)
        setApiErrorDetails(errorDetails)
        setShowApiErrorModal(true)
      }
    }
    fetchToimipaikka()
  }, [])

  // Debug logging for wrapper styles
  React.useEffect(() => {
    if (wrapperRef.current) {
      const computed = window.getComputedStyle(wrapperRef.current)
      console.log("OrganizationForm wrapper debug:", {
        width: computed.width,
        maxWidth: computed.maxWidth,
        marginLeft: computed.marginLeft,
        marginRight: computed.marginRight,
        classList: Array.from(wrapperRef.current.classList),
        viewportWidth: window.innerWidth,
      })
    }
  }, [])

  // Re-log on resize to see breakpoint changes
  React.useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const computed = window.getComputedStyle(wrapperRef.current)
        console.log("OrganizationForm wrapper resize:", {
          width: computed.width,
          maxWidth: computed.maxWidth,
          viewportWidth: window.innerWidth,
        })
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Disable scrolling when modal is open
  React.useEffect(() => {
    if (submitMessage) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = ""
    }
  }, [submitMessage])

  const organizationTypeOptions = [
    { label: t("organizationForm.organizationType.select"), value: "" },
    { label: t("organizationForm.organizationType.municipality"), value: "municipality" },
    { label: t("organizationForm.organizationType.other"), value: "other" },
    { label: t("organizationForm.organizationType.wellbeing_area"), value: "wellbeing_area" },
    { label: t("organizationForm.organizationType.insurance_company"), value: "insurance_company" },
    { label: t("organizationForm.organizationType.community"), value: "community" },
    { label: t("organizationForm.organizationType.company"), value: "company" },
  ]

  const customerRelationshipOptions = [
    { label: t("organizationForm.customerRelationship.select"), value: "" },
    {
      label: t("organizationForm.customerRelationship.hansel_occupational"),
      value: "hansel_occupational",
    },
    {
      label: t("organizationForm.customerRelationship.public_sector"),
      value: "public_sector",
    },
    {
      label: t("organizationForm.customerRelationship.local_occupational"),
      value: "local_occupational",
    },
    {
      label: t("organizationForm.customerRelationship.sickness_fund"),
      value: "sickness_fund",
    },
    { label: t("organizationForm.customerRelationship.insurance"), value: "insurance" },
    {
      label: t("organizationForm.customerRelationship.national_occupational"),
      value: "national_occupational",
    },
  ]

  const subjectOptions = [
    { label: t("organizationForm.subject.select"), value: "" },
    { label: t("organizationForm.subject.appointment_booking"), value: "appointment_booking" },
    { label: t("organizationForm.subject.customer_experience"), value: "customer_experience" },
    {
      label: t("organizationForm.subject.customer_relationship_management"),
      value: "customer_relationship_management",
    },
    { label: t("organizationForm.subject.remote_consultation"), value: "remote_consultation" },
    { label: t("organizationForm.subject.extranet_service"), value: "extranet_service" },
    {
      label: t("organizationForm.subject.direct_access_physical_therapist"),
      value: "direct_access_physical_therapist",
    },
    { label: t("organizationForm.subject.pricing"), value: "pricing" },
    {
      label: t("organizationForm.subject.treatment_and_examinations"),
      value: "treatment_and_examinations",
    },
    { label: t("organizationForm.subject.invoicing"), value: "invoicing" },
    { label: t("organizationForm.subject.medical_applications"), value: "medical_applications" },
    {
      label: t("organizationForm.subject.marketing_and_communication"),
      value: "marketing_and_communication",
    },
    { label: t("organizationForm.subject.other"), value: "other" },
    { label: t("organizationForm.subject.sales"), value: "sales" },
    { label: t("organizationForm.subject.supply_of_services"), value: "supply_of_services" },
    { label: t("organizationForm.subject.sirius"), value: "sirius" },
    { label: t("organizationForm.subject.agreement_issues"), value: "agreement_issues" },
    {
      label: t("organizationForm.subject.electronically_submitted_employee_info"),
      value: "electronically_submitted_employee_info",
    },
    { label: t("organizationForm.subject.oma_terveys_app"), value: "oma_terveys_app" },
    {
      label: t("organizationForm.subject.referrals_made_at_terveystalo"),
      value: "referrals_made_at_terveystalo",
    },
    { label: t("organizationForm.subject.data_protection"), value: "data_protection" },
    { label: t("organizationForm.subject.website"), value: "website" },
    {
      label: t("organizationForm.subject.environmental_responsibility"),
      value: "environmental_responsibility",
    },
  ]

  const menuItemOptions = [
    { label: t("organizationForm.menuItem.select"), value: "" },
    {
      label: t("organizationForm.menuItem.customer_service_desk"),
      value: "customer_service_desk",
    },
    {
      label: t("organizationForm.menuItem.laboratory_examinations"),
      value: "laboratory_examinations",
    },
    {
      label: t("organizationForm.menuItem.surgery_and_operations"),
      value: "surgery_and_operations",
    },
    { label: t("organizationForm.menuItem.imaging_examinations"), value: "imaging_examinations" },
    { label: t("organizationForm.menuItem.screening"), value: "screening" },
    { label: t("organizationForm.menuItem.oral_health"), value: "oral_health" },
    { label: t("organizationForm.menuItem.partner_service"), value: "partner_service" },
    { label: t("organizationForm.menuItem.appointment"), value: "appointment" },
    { label: t("organizationForm.menuItem.video_consultation"), value: "video_consultation" },
    { label: t("organizationForm.menuItem.common"), value: "common" },
  ]

  const wishToReceiveResponseOptions = [
    { label: t("wishToReceiveReply.select"), value: "" },
    { label: t("wishToReceiveReply.yes"), value: "yes" },
    { label: t("wishToReceiveReply.no"), value: "no" },
  ]

  // Create toimipaikka options from fetched list
  const toimipaikkaOptions = useMemo(() => {
    return [
      { label: t("organizationForm.toimipaikkaSelect"), value: "" },
      ...toimipaikkaList.map((toimipaikka) => {
        // Translate special toimipaikka names, fallback to original name if no translation exists
        const translationKey = `toimipaikkaNames.${toimipaikka.name}` as const
        const translatedName = t(translationKey, { defaultValue: toimipaikka.name })
        return {
          label: translatedName,
          value: toimipaikka.id,
        }
      }),
    ]
  }, [toimipaikkaList, t])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSelectChange = (name: keyof OrganizationFormState) => (e: ChangeEvent) => {
    const value = (e.target as unknown as FormElement).value
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage("")

    // Validation
    if (!formState.organizationType) {
      setSubmitMessage(t("error.typeRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.organizationName) {
      setSubmitMessage(t("error.nameRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.subject) {
      setSubmitMessage(t("error.subjectRequired"))
      setIsSubmitting(false)
      return
    }

    // If not authenticated, name is required
    if (!isLoggedIn && (!formState.name || formState.name.trim().length === 0)) {
      setSubmitMessage(t("error.nameRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.wishToReceiveResponse) {
      setSubmitMessage(t("error.subjectRequired")) // Using generic error for now
      setIsSubmitting(false)
      return
    }

    if (!formState.email) {
      setSubmitMessage(t("error.emailRequired"))
      setIsSubmitting(false)
      return
    }

    // Toimipaikka is required
    if (!formState.toimipaikka) {
      setSubmitMessage(t("error.toimipaikkaRequired"))
      setIsSubmitting(false)
      return
    }

    try {
      // Convert dateOfOccurrence string to Date object if it exists
      const dateOfOccurrence = formState.dateOfOccurrence
        ? new Date(formState.dateOfOccurrence)
        : undefined

      // Check if we have files to upload
      if (files.length > 0) {
        // Use FormData for multipart/form-data upload
        const formData = new FormData()

        // Prepare data
        const orgData = isLoggedIn
          ? (() => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { name: _name, ...data } = formState
              return { ...data, dateOfOccurrence }
            })()
          : { ...formState, dateOfOccurrence }

        // Append each form field
        Object.entries(orgData).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            formData.append(key, value instanceof Date ? value.toISOString() : String(value))
          }
        })

        // Append files
        files.forEach((file) => {
          formData.append("files", file)
        })

        // Make direct API call with FormData
        const backendUrl = config.backendHost
        const endpoint = isLoggedIn ? "/organization" : "/organization/anonymous"
        const user = await authClient.getUser()
        const response = await fetch(`${backendUrl}${endpoint}`, {
          method: "POST",
          body: formData,
          headers: isLoggedIn
            ? {
                Authorization: `Bearer ${user?.access_token}`,
                ...(user?.id_token ? { "X-Id-Token": user.id_token } : {}),
              }
            : {},
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
      } else {
        // No files - use regular JSON submission
        if (isLoggedIn) {
          // Authenticated submission - exclude name field
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { name: _name, ...feedbackData } = formState
          await client.submitOrganizationFeedback({
            organizationType: feedbackData.organizationType as OrganizationType,
            organizationName: feedbackData.organizationName!,
            businessId: feedbackData.businessId,
            customerRelationship: feedbackData.customerRelationship as
              | CustomerRelationshipType
              | undefined,
            subject: feedbackData.subject as OrganizationSubjectType,
            invoiceNumber: feedbackData.invoiceNumber,
            menuItem: feedbackData.menuItem as OrganizationMenuItemType | undefined,
            dateOfOccurrence,
            description: feedbackData.description,
            wishToReceiveResponse: feedbackData.wishToReceiveResponse as WishToReceiveResponseValue,
            email: feedbackData.email!,
            toimipaikka: feedbackData.toimipaikka!,
          } as OrganizationFeedback)
        } else {
          // Anonymous submission - include name field
          await client.submitAnonymousOrganizationFeedback({
            organizationType: formState.organizationType as OrganizationType,
            organizationName: formState.organizationName!,
            businessId: formState.businessId,
            customerRelationship: formState.customerRelationship as
              | CustomerRelationshipType
              | undefined,
            subject: formState.subject as OrganizationSubjectType,
            invoiceNumber: formState.invoiceNumber,
            menuItem: formState.menuItem as OrganizationMenuItemType | undefined,
            dateOfOccurrence,
            description: formState.description,
            wishToReceiveResponse: formState.wishToReceiveResponse as WishToReceiveResponseValue,
            name: formState.name!,
            email: formState.email!,
            toimipaikka: formState.toimipaikka!,
          } as AnonymousOrganizationFeedback)
        }
      }

      setSubmitMessage(t("success.feedbackSubmitted"))
      // Reset form
      setFormState(organizationFormInitialState)
      setFiles([]) // Clear files
    } catch (error) {
      console.error(error)
      // Check if it's a 401 authentication error
      if (error && typeof error === "object" && "response" in error) {
        const response = (error as { response?: { status?: number } }).response
        if (response?.status === 401) {
          setSubmitMessage(t("error.authenticationFailed"))
          setIsSubmitting(false)
          return
        }
      }
      setSubmitMessage(
        `${t("error.generic")}: ${error instanceof Error ? error.message : t("error.submitFailed")}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading state while restoring form data
  if (isRestoring) {
    return (
      <div className="min-h-screen bg-coal04 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lake mx-auto mb-4"></div>
          <p className="text-coal65">{t("form.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-coal04">
        <div ref={wrapperRef} className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("organizationForm.title")}</Title>
              <Lead>
                <Trans
                  i18nKey="organizationForm.lead"
                  components={{
                    red: <span style={{ color: "#CE0F69" }} />,
                  }}
                />
              </Lead>
            </div>
          </header>
          <main className="s:px-6 m:px-8">
            <div
              className="bg-woolhead rounded-lg shadow p-2"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <Title as="h2" size="l" className="mb-2">
                {t("organizationForm.formInfo")}
              </Title>

              {/* Form fields will be added here */}
              <div className="space-y-2 mb-4">
                <Select
                  id="organizationType"
                  name="organizationType"
                  label={t("organizationForm.organizationTypeLabel")}
                  options={organizationTypeOptions}
                  value={formState.organizationType}
                  onChange={handleSelectChange("organizationType")}
                  required
                />

                <TextField
                  id="organizationName"
                  name="organizationName"
                  label={t("organizationForm.organizationNameLabel")}
                  value={formState.organizationName}
                  onChange={handleInputChange}
                  required
                />

                <TextField
                  id="businessId"
                  name="businessId"
                  label={t("organizationForm.businessIdLabel")}
                  placeholder={t("organizationForm.businessIdPlaceholder")}
                  value={formState.businessId}
                  onChange={handleInputChange}
                />

                <Select
                  id="customerRelationship"
                  name="customerRelationship"
                  label={t("organizationForm.customerRelationshipLabel")}
                  options={customerRelationshipOptions}
                  value={formState.customerRelationship}
                  onChange={handleSelectChange("customerRelationship")}
                />

                <Title as="h2" size="l" className="mt-6 mb-2">
                  {t("organizationForm.feedbackInfo")}
                </Title>

                <Select
                  id="subject"
                  name="subject"
                  label={t("organizationForm.subjectLabel")}
                  options={subjectOptions}
                  value={formState.subject}
                  onChange={handleSelectChange("subject")}
                  required
                />

                <TextField
                  id="invoiceNumber"
                  name="invoiceNumber"
                  label={t("organizationForm.invoiceNumberLabel")}
                  value={formState.invoiceNumber}
                  onChange={handleInputChange}
                />

                <Select
                  id="menuItem"
                  name="menuItem"
                  label={t("organizationForm.menuItemLabel")}
                  options={menuItemOptions}
                  value={formState.menuItem}
                  onChange={handleSelectChange("menuItem")}
                />

                <TextField
                  id="dateOfOccurrence"
                  name="dateOfOccurrence"
                  type="date"
                  label={t("organizationForm.dateOfOccurrenceLabel")}
                  value={formState.dateOfOccurrence}
                  onChange={handleInputChange}
                />

                <TextField
                  id="description"
                  name="description"
                  label={t("organizationForm.descriptionLabel")}
                  as="textarea"
                  value={formState.description}
                  onChange={handleInputChange}
                  rows={4}
                />

                <div className="space-y-2 mb-4">
                  <label
                    htmlFor="file-upload-org"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("form.attachments")}
                  </label>
                  <input
                    id="file-upload-org"
                    name="files"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  {files.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      {t("form.filesSelected", { count: files.length })}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{t("form.fileUploadHint")}</p>
                </div>

                <Select
                  id="wishToReceiveResponse"
                  name="wishToReceiveResponse"
                  label={t("organizationForm.wishToReceiveResponseLabel")}
                  options={wishToReceiveResponseOptions}
                  value={formState.wishToReceiveResponse}
                  onChange={handleSelectChange("wishToReceiveResponse")}
                  required
                />

                <Autocomplete
                  id="toimipaikka"
                  name="toimipaikka"
                  label={t("organizationForm.toimipaikkaLabel")}
                  placeholder={t("organizationForm.toimipaikkaPlaceholder")}
                  options={toimipaikkaOptions}
                  value={formState.toimipaikka || ""}
                  onChange={(value) => setFormState({ ...formState, toimipaikka: value })}
                  required
                />

                {/* Show Name field only for unauthenticated users */}
                {!isLoggedIn && (
                  <TextField
                    id="name"
                    name="name"
                    label={t("organizationForm.nameLabel")}
                    value={formState.name || ""}
                    onChange={handleInputChange}
                    required
                    placeholder={t("organizationForm.namePlaceholder")}
                  />
                )}

                <TextField
                  id="email"
                  name="email"
                  type="email"
                  label={t("organizationForm.emailLabel")}
                  value={formState.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <p className="text-coal65 mb-6">
                {t("form.privacyNotice")}
                <a href={t("form.privacyStatementUrl")} className="link">
                  {t("form.privacyStatement")}
                </a>
                {t("form.privacyCustomers")}
              </p>

              {/* Debugging block - only shown when DEBUG_FORM_VALUES is set */}
              {config.debugFormValues && (
                <pre className="p-4 bg-coal15 text-coal85 rounded-md my-4">
                  <code>{JSON.stringify(formState, null, 2)}</code>
                </pre>
              )}

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t("form.submitting") : t("submit")}
              </Button>
            </div>
          </main>
        </div>
      </div>

      {/* Success/Error Modal */}
      {submitMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "32px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              maxWidth: "500px",
              textAlign: "center",
            }}
          >
            <p style={{ marginBottom: "24px", fontSize: "1.125rem" }}>{submitMessage}</p>
            {isLoggedIn ? (
              <Button
                type="button"
                onClick={() =>
                  authClient.signoutRedirect({
                    post_logout_redirect_uri: authConfig.post_logout_redirect_uri,
                  })
                }
                variant="secondary"
              >
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Icon name="LogOut" color="lake" />
                  {t("logout")}
                </span>
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => (window.location.href = "https://www.terveystalo.com")}
              >
                {t("close")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* API Error Modal */}
      {showApiErrorModal && (
        <Modal
          open={showApiErrorModal}
          onClose={() => setShowApiErrorModal(false)}
          title={t("error.apiErrorTitle")}
          closeButtonLabel={t("close")}
        >
          <div className="flex flex-col items-center text-center space-y-3">
            <p>{t("error.apiErrorMessage")}</p>
            {apiErrorDetails && (
              <div className="bg-coal04 p-3 rounded w-full">
                <div className="flex flex-col items-center gap-2">
                  <div className="text-red-600" style={{ transform: "scale(3)" }}>
                    <Icon name="AlertCircle" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-mono text-red-600">{apiErrorDetails}</p>
                    <p className="text-sm font-semibold">Failed to fetch Assets</p>
                  </div>
                </div>
              </div>
            )}
            <Button onClick={() => setShowApiErrorModal(false)} className="mt-2">
              {t("error.close")}
            </Button>
          </div>
        </Modal>
      )}
    </form>
  )
}

export default OrganizationForm
